/**
 * The script load report (D344, provisional): one small beacon when `peak-checkout.js` failed to
 * load, had not run 8 s after the inline stub, or started more than 6 s after the navigation —
 * the page views on which a checkout tap could reach Shopify's own checkout because Peak was not
 * there yet.
 *
 * Why: a store still takes orders in Shopify's checkout while Peak is live (ModernoMilano #472895,
 * 2026-10-26: iPhone Safari, Apple Pay in Shopify's checkout, nothing at all on Peak's server).
 * With the script running, that theme shows no wallet button and Checkout goes to Peak; so on
 * that page view the script failed or was late, and a script cannot report its own absence. The
 * inline early-tap stub (D343) could: it ran with the HTML, before the deferred tag. Since D360
 * Peak no longer prints the stub, so `script_error` and `script_late` come only from the themes
 * that pasted it before and still carry it; every store sends the script's own
 * `script_ready_late`, and the endpoint accepts all three. The reports are kept as `StorefrontLoadReport` rows with the Shopify cart token, so a
 * Shopify order can later be matched to the page view that let it through.
 *
 * Three reasons, at most one of each per page view:
 *
 * | Reason | Sent by | When |
 * | --- | --- | --- |
 * | `script_error` | an installed stub (none printed since D360) | the tag's `error` event (`stage: 'load'`: blocked, 4xx/5xx, network), or its `load` event with the script never having set `__peakCheckoutLoaded` (`stage: 'run'`: it threw, or was not our script) |
 * | `script_late` | an installed stub | {@link SCRIPT_LATE_AFTER_MS} after the stub ran, the script has neither run nor failed |
 * | `script_ready_late` | the script | it started more than {@link SCRIPT_READY_LATE_AFTER_MS} after the navigation |
 *
 * A fast, healthy page view sends nothing. The stub's `script_not_loaded` (D343) carries the same
 * load fields, so a tap it let through to Shopify can be matched too.
 *
 * Payload ({@link PeakLoadReport}): the public key and shop domain (already in the theme source),
 * the reason, the sender's version, the delay since navigation start, the page path **without**
 * its query or fragment, the Shopify cart token **without** its `?key=` secret (from the `cart`
 * cookie, {@link cartTokenFromCookie}), the page's `visibilityState`, and `touch: true` on a touch
 * device. The device class and the in-app browser are worked out by the server from the beacon's
 * own `User-Agent` header with the checkout's rules (`inAppBrowser`, `expressDevice`), so neither
 * the stub nor the bundle carries a copy of them; the user agent itself is never stored. Never an
 * email, an IP or a query string. The cart token is the one identifier here, sent on purpose: it
 * is what a Shopify order carries as `cart_token`, and the only way to tie a native order to this
 * page view (`docs/STOREFRONT.md` §5).
 *
 * The stub never imported this module (it is ES5 and inline) and carried its own copy of the
 * cookie and delay rules; those copies are frozen in the themes that have it
 * (`tests/fixtures/storefront/installed-early-tap-stubs.ts`). The constants below that describe
 * the stub ({@link SCRIPT_LATE_AFTER_MS}) document what those copies do; nothing here sends them.
 *
 * Sent to the fallback endpoint (`POST {service}/api/storefront/fallback`), which keeps its
 * origin check and rate limit for these too (`src/app/api/storefront/fallback/handler.ts`).
 */

/** The reasons of a load report; any other reason is a fallback report (D247). */
export const PEAK_LOAD_REPORT_REASONS = [
  'script_error',
  'script_late',
  'script_ready_late',
] as const;
export type PeakLoadReportReason = (typeof PEAK_LOAD_REPORT_REASONS)[number];

/**
 * An installed stub's deadline, from the moment it runs: 8 s, the same as its held-tap deadline (D343), so
 * a `script_late` page view is one on which a tap made when the stub ran would already have been
 * let through to Shopify. `docs/decisions/D344.md` has the reasoning.
 */
export const SCRIPT_LATE_AFTER_MS = 8000;

/**
 * The script's own threshold, from navigation start: above it, `script_ready_late`. 6 s is half
 * again Google's "poor" Largest Contentful Paint line (4 s): by then the theme's Checkout button
 * has long been on screen and tappable (`docs/decisions/D344.md`).
 */
export const SCRIPT_READY_LATE_AFTER_MS = 6000;

/** A delay above this is clamped: a tab left in the background for an hour says nothing more. */
export const MAX_LOAD_DELAY_MS = 3_600_000;

/** `document.visibilityState` when the report was made. */
export const PEAK_VISIBILITY_STATES = ['visible', 'hidden'] as const;
export type PeakVisibilityState = (typeof PEAK_VISIBILITY_STATES)[number];

/** For `script_error`: the tag never loaded (`load`), or loaded and never signalled (`run`). */
export const PEAK_LOAD_STAGES = ['load', 'run'] as const;
export type PeakLoadStage = (typeof PEAK_LOAD_STAGES)[number];

/** The longest cart token kept; Shopify documents no length ("any format"), so be generous. */
export const MAX_CART_TOKEN_LENGTH = 200;

export type PeakLoadReport = {
  publicKey: string;
  shopDomain: string;
  reason: PeakLoadReportReason;
  /** The bundle's version, or an installed stub's (`stub-2`, `stub-3`). */
  version: string;
  /** Ms since navigation start (since activation for a prerendered page), when it was made. */
  delayMs: number;
  /** `location.pathname`: never the query or the fragment. */
  path: string;
  /** The Shopify cart token without its `?key=` part; absent when the cookie is not readable. */
  cartToken?: string;
  visibility?: PeakVisibilityState;
  /** `navigator.maxTouchPoints > 1`: tells an iPad on the desktop site from a Mac. */
  touch?: true;
  /** `script_error` only. */
  stage?: PeakLoadStage;
  /** An inline early-tap stub ran on this page (a theme pasted between D343 and D360); else absent. */
  stub?: true;
};

/**
 * The Shopify cart token from `document.cookie`, without its secret: the `cart` cookie's value
 * cut before the first `?` (raw or encoded as `%3F`), then URL-decoded. Shopify: "The cart ID
 * consists of a token and a secret key parameter in the form of `<token>?key=<secret>`" and
 * "Never expose the secret part of the ID"
 * (https://shopify.dev/docs/storefronts/headless/building-with-the-storefront-api/cart/manage);
 * the cut is the same as the order's `shopify-cart-token` (`shopifyCartTokenForOrder`,
 * `src/lib/shopify/orders/build-order-input.ts`), so the two match. `undefined` when there is no
 * cart cookie (no cart yet), it is empty or too long, or it is not readable (an `HttpOnly` cookie
 * is not in `document.cookie`).
 */
export function cartTokenFromCookie(cookie: string): string | undefined {
  const match = /(?:^|;\s*)cart=([^;]*)/.exec(cookie);
  if (match === null) return undefined;
  let token = (match[1] ?? '').split(/\?|%3f/i)[0] ?? '';
  try {
    token = decodeURIComponent(token);
  } catch {
    // A malformed escape: the raw value is still the token's best spelling.
  }
  return token === '' || token.length > MAX_CART_TOKEN_LENGTH ? undefined : token;
}

/** The slice of `window.performance` the delay reads. */
export type PeakPerformanceLike = {
  now: () => number;
  getEntriesByType?: (type: string) => unknown[];
};

/**
 * Ms since this page's navigation started, as a whole number in `[0, MAX_LOAD_DELAY_MS]`, or
 * `null` without `performance`. A prerendered page counts from its activation
 * (`PerformanceNavigationTiming.activationStart`), so a script that ran during the prerender is
 * never late.
 */
export function loadDelayMs(performance: PeakPerformanceLike | null | undefined): number | null {
  try {
    if (!performance || typeof performance.now !== 'function') return null;
    let start = 0;
    const entry =
      typeof performance.getEntriesByType === 'function'
        ? (performance.getEntriesByType('navigation')[0] as
            { activationStart?: unknown } | undefined)
        : undefined;
    if (entry && typeof entry.activationStart === 'number' && entry.activationStart > 0) {
      start = entry.activationStart;
    }
    const delay = Math.round(performance.now() - start);
    return Math.min(Math.max(delay, 0), MAX_LOAD_DELAY_MS);
  } catch {
    return null;
  }
}

/** What the script's own `script_ready_late` reads from the browser. */
export type PeakLoadEnvironment = {
  /** {@link loadDelayMs} read as the script started executing. */
  delayMs: number | null;
  maxTouchPoints: number;
  cookie: string;
  path: string;
  visibility: string;
  /** `window.__peakCheckoutEarly` is set: an installed stub ran on this page. */
  stub: boolean;
};

/**
 * The `script_ready_late` report for this page view, or `null` when the script started within
 * {@link SCRIPT_READY_LATE_AFTER_MS} (or the delay cannot be read): a normal page view sends
 * nothing.
 */
export function readyLateReport(
  base: { publicKey: string; shopDomain: string; version: string },
  env: PeakLoadEnvironment,
): PeakLoadReport | null {
  const { delayMs } = env;
  if (delayMs === null || delayMs <= SCRIPT_READY_LATE_AFTER_MS) return null;
  const report: PeakLoadReport = { ...base, reason: 'script_ready_late', delayMs, path: env.path };
  const cartToken = cartTokenFromCookie(env.cookie);
  if (cartToken !== undefined) report.cartToken = cartToken;
  if (env.visibility === 'visible' || env.visibility === 'hidden') {
    report.visibility = env.visibility;
  }
  if (env.maxTouchPoints > 1) report.touch = true;
  if (env.stub) report.stub = true;
  return report;
}
